export type ToolStatus = "experimental" | "beta" | "production";
export type ExecutionMode = "numeric" | "browser" | "hybrid" | "worker";

export type PerformanceProfile = {
  version: number;
  status: "unbenchmarked" | "provisional" | "benchmarked";
  inputMetrics: string[];
  localHardLimit?: string;
  cancellation: boolean;
  notes: string[];
};

export type ToolCatalogDefinition = {
  id: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  status: ToolStatus;
  execution: ExecutionMode;
};

export type ToolDetails = {
  acceptedFormats: string[];
  outputFormats: string[];
  privacyDisclosure: string;
  limitations: string[];
  performanceProfile: PerformanceProfile;
};

export type ToolDefinition = ToolCatalogDefinition & ToolDetails;

export type ToolCategory = {
  slug: string;
  title: string;
  description: string;
};

export type TaskModeLevel = "basic" | "advanced";
export type TaskOutputKind = "none" | "report" | "geometry-2d" | "geometry-3d";

export type TaskModeDefinition = {
  id: string;
  operationId: string;
  label: string;
  level: TaskModeLevel;
  acceptedInputs: string[];
  outputKind: TaskOutputKind;
  previewRequired: boolean;
  executionPolicy: "local" | "local-or-remote";
};

export type TaskPageDefinition = {
  id: string;
  path: string;
  category: "models" | "make" | "setup" | "plan" | "direct";
  title: string;
  pickerHint: string;
  seoTitle: string;
  seoDescription: string;
  aliases: string[];
  legacyRoutes: string[];
  disclosure: { privacy: string; limits: string; maturity: string };
  modes: TaskModeDefinition[];
};

export type LocalFileMetadata = {
  format: string;
  bytes: number;
  triangles?: number;
  vertices?: number;
  components?: number;
  complexity?: number;
};

export type ExecutionPreflight = {
  metadata: LocalFileMetadata;
  localEligible: boolean;
  remoteAdapterEligible: boolean;
  reasonCode: "LOCAL_SAFE" | "LOCAL_LIMIT_EXCEEDED" | "REMOTE_LIMIT_EXCEEDED" | "REMOTE_NOT_REGISTERED" | "UNSUPPORTED_FORMAT";
  remoteAdapterId?: string;
};

export type DiagnosticGraph = {
  version: number;
  nodes: Array<{
    id: string;
    kind: "question" | "suggestion" | "safety-stop" | "resolved" | "escalation";
    answers?: Array<{ value: "yes" | "no" | "not-sure" | "not-applicable" | "worked" | "failed" | "unsafe"; next: string }>;
  }>;
};

export type TaskEntryPoint = {
  id: "models" | "make" | "setup" | "plan" | "help" | "share" | "sell";
  title: string;
  hint: string;
  path: string;
};

export type LegacyRouteDisposition =
  | { kind: "redirect"; targetPath: string }
  | { kind: "gone"; reason: string };

export const categories: ToolCategory[] = [
  { slug: "inspect", title: "Inspect", description: "Open, measure, and understand model and print files." },
  { slug: "repair", title: "Repair", description: "Fix, split, transform, and prepare geometry." },
  { slug: "convert", title: "Convert", description: "Move between documented mesh, CAD, package, and image formats." },
  { slug: "create", title: "Create", description: "Generate functional, personalized, and calibration models." },
  { slug: "mold", title: "Mold & Cast", description: "Analyze masters and create bounded molds, formwork, cores, gates, vents, and casting plans." },
  { slug: "calibrate", title: "Calibrate", description: "Build measured printer, material, and fit knowledge." },
  { slug: "calculate", title: "Calculate", description: "Estimate filament, flow, cost, time, and dimensional outcomes." },
  { slug: "plan", title: "Plan", description: "Check build fit, orientation, G-code, and production requirements." },
  { slug: "diagnose", title: "Diagnose", description: "Investigate print failures, logs, profiles, and dimensional problems." },
  { slug: "manage", title: "Manage", description: "Handle provenance, licenses, materials, jobs, and seller workflows." }
];

const retiredOperationIds = new Set([
  "amf-to-stl", "stl-to-amf", "off-to-stl", "stl-to-off", "local-diagnostics",
  "advanced-silicone-formwork", "matrix-mother-mold", "plaster-slip-cast", "vessel-core-kit", "matched-press",
  "guided-print-triage", "stringing-diagnostic", "warping-adhesion-diagnostic", "layer-shift-diagnostic", "extrusion-diagnostic",
]);

export const legacyTools: ToolCatalogDefinition[] = [
  {
    id: "step-to-3mf",
    slug: "step-to-3mf",
    title: "STEP to 3MF Tessellator",
    summary: "Tessellate bounded STEP assemblies into named millimeter mesh objects in a verified core 3MF package.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "step-to-stl",
    slug: "step-to-stl",
    title: "STEP to STL Tessellator",
    summary: "Transfer bounded STEP CAD geometry through OpenCascade and flatten the verified millimeter tessellation into binary STL.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "amf-to-stl",
    slug: "amf-to-stl",
    title: "AMF to STL Converter",
    summary: "Flatten bounded AMF 1.0–1.2 objects, volumes, units, and constellation instances into a verified millimeter binary STL.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "stl-to-amf",
    slug: "stl-to-amf",
    title: "STL to AMF Converter",
    summary: "Convert binary or ASCII STL millimeter geometry into a verified core AMF 1.2 XML mesh.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "off-to-stl",
    slug: "off-to-stl",
    title: "OFF to STL Converter",
    summary: "Validate and triangulate bounded plain ASCII OFF polygons into a verified binary STL.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "stl-to-off",
    slug: "stl-to-off",
    title: "STL to OFF Converter",
    summary: "Convert binary or ASCII STL triangles into a verified plain ASCII OFF mesh.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "glb-to-stl",
    slug: "glb-to-stl",
    title: "GLB to STL Converter",
    summary: "Flatten a bounded core glTF 2.0 scene and convert its meter-based triangle geometry to verified millimeter STL.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "stl-to-glb",
    slug: "stl-to-glb",
    title: "STL to GLB Converter",
    summary: "Convert binary or ASCII STL millimeter geometry into a verified core GLB 2.0 meter-based mesh.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "ply-to-stl",
    slug: "ply-to-stl",
    title: "PLY to STL Converter",
    summary: "Triangulate bounded ASCII or binary little-endian PLY geometry into a verified binary STL.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "stl-to-ply",
    slug: "stl-to-ply",
    title: "STL to PLY Converter",
    summary: "Convert binary or ASCII STL triangles into a verified binary little-endian PLY mesh.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "three-mf-to-stl",
    slug: "3mf-build-item-stl-extractor",
    title: "3MF Build Item & Component STL Extractor",
    summary: "Flatten selected core 3MF build items and components into verified millimeter STL files.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "multi-stl-to-3mf",
    slug: "multi-stl-core-3mf-packager",
    title: "Multi-STL Core 3MF Packager",
    summary: "Package up to 16 local STL meshes as separate millimeter build objects in one verified core 3MF assembly.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "spur-gear",
    slug: "involute-spur-gear",
    title: "Involute Spur Gear Generator",
    summary: "Generate a verified full-depth involute spur gear with module, teeth, pressure angle, backlash, fit-adjusted bore, and optional pair calculations.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "v-groove-pulley",
    slug: "single-v-groove-pulley",
    title: "Single V-Groove Pulley Generator",
    summary: "Generate a verified symmetric V-groove pulley with a fit-profile-adjusted plain shaft bore and explicit rotating-part limitations.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "hose-duct-reducer",
    slug: "circular-hose-duct-reducer",
    title: "Circular Hose & Duct Reducer Generator",
    summary: "Generate a verified hollow reducer with two fit-profile-adjusted female slip collars and a bounded tapered transition.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "drawer-grid-organizer",
    slug: "drawer-grid-organizer",
    title: "Drawer Grid Organizer Tray Generator",
    summary: "Generate a verified fit-profile-adjusted drawer tray with one connected floor, outer walls, and a uniform row-and-column divider grid.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "screw-lid-enclosure",
    slug: "screw-lid-electronics-enclosure",
    title: "Screw-Lid Electronics Enclosure Generator",
    summary: "Generate verified body and overlapping cap STLs with blind screw bosses, aligned clearance holes and counterbores, fit-profile skirt clearance, and optional lid vents.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "lobed-knob",
    slug: "lobed-hand-knob",
    title: "Lobed Hand Knob & Handwheel Generator",
    summary: "Generate a verified chamfered grip with configurable lobes and a clearance-adjusted round, D, hex, or square through-bore.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "hole-feature-detector",
    slug: "axis-hole-cylinder-detector",
    title: "Axis Hole & Cylinder Detector",
    summary: "Fit connected concave X/Y/Z-aligned cylindrical surface patches and report candidate diameter, span, circularity, bounds relationship, and nearby metric-clearance references.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "overhang-map",
    slug: "overhang-angle-map",
    title: "Overhang Angle & Surface Report",
    summary: "Measure oriented face angles, build-plate contact, and threshold-exceeding surface area without pretending to generate supports or predict a slicer.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "wall-thickness",
    slug: "wall-thickness-sampling",
    title: "Wall Thickness Sampling Report",
    summary: "Cast bounded inward normal rays from sampled STL triangle centroids to locate possible thin regions and export the full advisory evidence.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "hardware-pocket-composer",
    slug: "magnet-heat-set-insert-pocket-composer",
    title: "Magnet & Heat-Set Insert Pocket Composer",
    summary: "Subtract up to 16 measured cylindrical or tapered hardware pockets from a watertight STL with sampled surface entry and retained-stock verification.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "hollow-drain-generator",
    slug: "hollow-wall-drain-vent-generator",
    title: "Hollow Wall, Drain & Vent Generator",
    summary: "Create a measured inward-offset shell with verified cavity-to-exterior drain or vent openings and optional separately verified fit-clearance plugs.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "emboss-deboss-label",
    slug: "emboss-deboss-text-label-composer",
    title: "Emboss & Deboss Text Label Composer",
    summary: "Add or recess a deterministic block-text label on a sampled planar global-axis face and export a separately verified one-piece STL.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mesh-decimator",
    slug: "mesh-decimator-deviation-map",
    title: "Mesh Decimator & Deviation Map",
    summary: "Reduce a closed STL by triangle count, retained percentage, or binary file size while enforcing topology and bidirectional sampled surface-deviation checks.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "isotropic-remesh",
    slug: "isotropic-remesh-triangle-quality",
    title: "Isotropic Remesh & Triangle Quality",
    summary: "Retessellate a closed STL toward a physical edge length, improve bounded skinny triangles, and verify achieved quality, topology, and bidirectional sampled deviation.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "smooth-denoise",
    slug: "smooth-denoise-shrinkage-report",
    title: "Smooth & Denoise with Shrinkage Report",
    summary: "Apply bounded Laplacian, shrink-resistant Taubin, or bilateral normal-direction denoising while measuring topology, displacement, shrinkage, quality, and sampled deviation.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "flatten-base-pedestal",
    slug: "flatten-base-add-pedestal-datum",
    title: "Flatten Base & Add Pedestal Datum",
    summary: "Rigidly orient any global source face to the build plate, trim and cap a real planar base, optionally union a verified overlapping pedestal, and report the resulting datum.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "texture-displacement",
    slug: "texture-displacement-mapper",
    title: "Texture & Displacement Mapper",
    summary: "Decode a local grayscale image into a bounded heightfield, emboss or deboss a verified global-axis source surface, and report pitch, contact, retained-wall, topology, and volume evidence.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "color-inlay-splitter",
    slug: "multi-color-planar-inlay-splitter",
    title: "Multi-Color Planar Inlay Splitter",
    summary: "Read uniform core 3MF color/material regions, turn one planar outward region into separate flush inlay solids, subtract a tolerance-fitted cavity from the base, and verify every STL.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "paint-material-regions",
    slug: "paint-material-region-editor",
    title: "3MF Paint & Material Region Editor",
    summary: "Brush, fill, or rule-select exact STL triangles, author uniform standard 3MF color regions, preview them in WebGL2, and round-trip every assignment before download.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "boolean-csg-studio",
    slug: "mesh-boolean-union-subtract-intersect",
    title: "Mesh Boolean Union, Subtract & Intersect Studio",
    summary: "Combine two closed STL solids with measured union, A-minus-B subtraction, or intersection; transform mesh B, compare the input overlay with the verified output, and download a separate reopened STL.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mold-intake-repair",
    slug: "master-intake-repair",
    title: "Mold Master Intake & Repair",
    summary: "Diagnose bounded STL, OBJ, or selected core 3MF geometry, create a separate millimeter STL, and explicitly generate conservative repair candidates with before/after deltas.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mold-safety-record",
    slug: "process-risk-checklist",
    title: "Mold Process Risk Checklist",
    summary: "Create an auditable context-specific record for release, leakage, surface, heat, cure, shrink, clamping, and unsupported uses.",
    category: "mold",
    status: "experimental",
    execution: "numeric",
  },
  {
    id: "matrix-mother-mold",
    slug: "matrix-mother-mold-system",
    title: "Conformal Matrix & Mother-Mold Generator",
    summary: "Generate two flexible conformal liner halves and two registered, flanged rigid support-shell halves around a watertight STL master.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "advanced-silicone-formwork",
    slug: "advanced-silicone-formwork-support-system",
    title: "Advanced Silicone Formwork & Support System",
    summary: "Generate split conformal liners and rigid shells, real band channels, a registered removable base, printable seam clamps, a connected funnel, seven numbered STLs, and one verified 3MF assembly.",
    category: "mold",
    status: "experimental",
    execution: "hybrid",
  },
  {
    id: "plaster-slip-cast",
    slug: "plaster-slip-casting-mold-blocks",
    title: "Plaster Slip-Casting Mold Block Generator",
    summary: "Generate two verified shrink-compensated negative blocks with a connected trough, male keys, sockets, and numbered assembly order.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "vessel-core-kit",
    slug: "vessel-planter-core-kit",
    title: "Vessel & Planter Core Kit Generator",
    summary: "Generate a verified tapered outer form, wall/base-controlling inner core, and crossed centering yoke for a circular vessel.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "matched-press",
    slug: "matched-die-piston-press",
    title: "Matched Die & Piston Press Generator",
    summary: "Generate a verified female die, clearance-fit stopped piston, and optional connected ejector plug from one flat-backed press profile.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "multi-cavity-tray",
    slug: "multi-cavity-open-pour-tray",
    title: "Multi-Cavity Open-Pour Tray Generator",
    summary: "Repeat one flat-backed master into a verified labeled cavity array with bounded spacing, common runner grooves, and a tapered pour cup.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "two-part-mold",
    slug: "two-part-rigid-block-mold",
    title: "Two-Part Rigid Block Mold Generator",
    summary: "Generate two independently verified keyed mold halves with editable process features and clustered surface-high-point vent suggestions.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "contour-multi-piece-mold",
    slug: "contour-three-four-piece-rigid-mold",
    title: "Contour 3/4-Piece Rigid Mold Generator",
    summary: "Partition one negative mold along an editable non-planar height contour and export three or four numbered, independently verified keyed STL pieces.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mold-channel-network",
    slug: "runner-gate-sprue-vent-network",
    title: "3D Runner, Gate, Sprue & Vent Network Generator",
    summary: "Edit a bounded 3D channel graph, prove every path reaches the actual cavity and stock exterior, and export two verified keyed mold halves.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "silicone-formwork",
    slug: "silicone-mold-box-formwork",
    title: "Silicone Mold Box & Formwork Generator",
    summary: "Generate verified rectangular or round open-top printable formwork sized from a watertight positive master, with placement and silicone-volume reports.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mold-feasibility",
    slug: "feasibility-parting-advisor",
    title: "Mold Feasibility & Parting Advisor",
    summary: "Rank seven paired pull directions with BVH-tested occlusion, low-draft area, estimated stock, build fit, and a sampled advisory heatmap.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mold-mix",
    slug: "material-mix-calculator",
    title: "Mold Material & Mix Calculator",
    summary: "Turn cavity volume into an auditable A:B component, waste, cost, batch, and shrinkage worksheet for two-part casting materials.",
    category: "mold",
    status: "experimental",
    execution: "numeric",
  },
  {
    id: "open-pour-mold",
    slug: "open-pour-negative-mold",
    title: "Open-Pour Negative Mold Generator",
    summary: "Subtract a watertight, flat-backed positive master from measured mold stock and export a verified open-top negative STL.",
    category: "mold",
    status: "experimental",
    execution: "browser",
  },
  {
    id:"job-manifest",slug:"print-job-manifest",title:"Print Job Manifest Generator",summary:"Create a bounded, reviewable JSON manifest of parts, copies, material, profile, orientation, source hashes, and notes.",category:"manage",status:"experimental",execution:"numeric",
  },
  {
    id: "local-diagnostics",
    slug: "local-capability-support-report",
    title: "Local Capability & Support Report",
    summary: "Preview browser capabilities and an optional coarse worker benchmark in a scrubbed, manually downloadable support payload.",
    category: "manage",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "annular-spacer",
    slug: "washer-spacer-sleeve-generator",
    title: "Washer, Spacer & Sleeve Generator",
    summary: "Generate a verified annular washer, short spacer, or long sleeve from OD, ID, length, and circle resolution.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "bed-mesh",
    slug: "bed-mesh-visualizer",
    title: "Klipper & Marlin Bed Mesh Visualizer",
    summary: "Import a bounded bed mesh, visualize raw and tilt-removed height, identify residual outliers, and compare an identical baseline grid without contacting the printer.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "resonance-viewer",
    slug: "klipper-resonance-input-shaper-viewer",
    title: "Klipper Resonance & Input-Shaper Viewer",
    summary: "Visualize bounded Klipper-derived resonance spectra, separated local peaks, historical overlays, embedded shaper-response curves, and explicit console recommendations without controlling the printer.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "bed-tramming",
    slug: "bed-screw-tramming-assistant",
    title: "Bed Screw & Tramming Assistant",
    summary: "Parse bounded Klipper screws-tilt or Marlin G35 evidence, or calculate a manual reference-relative screw plan, without connecting to the printer or inventing missing geometry.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "extrusion-calibration",
    slug: "extruder-steps-rotation-distance-wizard",
    title: "Extruder Steps & Rotation-Distance Wizard",
    summary: "Calculate reviewed Klipper rotation_distance or Marlin E steps/mm corrections from repeated measured filament movement without connecting to or changing the printer.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "flow-ratio",
    slug: "flow-ratio-extrusion-multiplier-wizard",
    title: "Flow Ratio & Extrusion-Multiplier Wizard",
    summary: "Calculate method-correct OrcaSlicer or measured-wall filament flow-ratio candidates, generate a verified vase-mode test cube, and retain explicit derived-only history locally.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "temperature-tower",
    slug: "temperature-tower-generator",
    title: "Temperature Tower Generator",
    summary: "Generate a verified raised-label bridge/overhang tower and exact section-to-temperature layer map inside a bounded local worker without creating G-code or contacting a printer.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "retraction-tower",
    slug: "retraction-tower-generator",
    title: "Retraction Tower Generator",
    summary: "Generate a verified raised-label two-post retraction length or speed tower and exact section-to-layer map without creating G-code or changing a printer profile.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "pressure-advance-tower",
    slug: "pressure-linear-advance-corner-tower",
    title: "Pressure & Linear Advance Corner Tower",
    summary: "Generate a verified hollow corner tower, exact section/layer map, and inert Klipper or modern Marlin command rows without creating complete G-code or connecting to a printer.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "maximum-volumetric-flow",
    slug: "maximum-volumetric-flow-ramp-tower",
    title: "Maximum Volumetric-Flow Ramp Tower",
    summary: "Generate a verified straight-wall tower and exact flow/speed/layer map, then derive a review-gated material-profile candidate from the first physically failing section.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "cornering-control-tower",
    slug: "cornering-jerk-junction-deviation-tower",
    title: "Cornering, Jerk & Junction-Deviation Tower",
    summary: "Generate a verified corner tower and exact dialect-specific layer/command map for Klipper square-corner velocity or distinct Marlin junction-deviation/classic-jerk modes.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "acceleration-reach-tower",
    slug: "acceleration-reach-tower",
    title: "Acceleration Reach Tower",
    summary: "Generate a verified fixed-speed corner tower and exact Klipper or Marlin print-acceleration map while cornering and flow remain fixed.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "vfa-speed-tower",
    slug: "vfa-speed-resonance-tower",
    title: "VFA Speed & Resonance Tower",
    summary: "Generate a verified eight-facet, 25-layer-per-section speed tower and exact slicer map while acceleration, cornering, and extrusion remain fixed.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "layer-stack",
    slug: "layer-count-height-planner",
    title: "Layer Count & Height Planner",
    summary: "Round a requested model height up to a realizable first-layer-plus-regular-layer stack.",
    category: "calculate",
    status: "experimental",
    execution: "numeric",
  },
  {
    id: "dimensional-compensation",
    slug: "dimensional-scale-compensation",
    title: "Dimensional Scale Compensation",
    summary: "Calculate a uniform scale factor from a designed dimension, measured print, and desired target.",
    category: "calculate",
    status: "experimental",
    execution: "numeric",
  },
  {
    id: "resin-cost",
    slug: "resin-material-cost",
    title: "Resin Material Cost Calculator",
    summary: "Estimate resin volume, mass, material cost, consumables, and expected failure allowance.",
    category: "calculate",
    status: "experimental",
    execution: "numeric",
  },
  {
    id: "open-box",
    slug: "open-box-generator",
    title: "Open Box Generator",
    summary: "Generate a measured sharp-corner rectangular bin from outside dimensions, wall thickness, and floor thickness.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "geometry-compare",
    slug: "stl-geometry-compare",
    title: "STL Geometry Compare",
    summary: "Compare an original and revised STL by exact-coordinate triangle multiset, dimensions, surface area, and signed-volume estimate.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "axis-orientation",
    slug: "axis-orientation-advisor",
    title: "Axis Orientation Advisor",
    summary: "Rank all six axis-aligned build directions by winding-aware downward area, height, and bounding-box footprint.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "center-of-mass-stability",
    slug: "center-of-mass-stability-checker",
    title: "Center of Mass & Stability Checker",
    summary: "Integrate a closed uniform-density STL, measure six exact contact hulls, and compare signed stability margins and geometric tip angles.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "section-contour-viewer",
    slug: "section-contour-outline-viewer",
    title: "Section & Contour Outline Viewer",
    summary: "Intersect an STL with an exact X, Y, or Z plane, diagnose open and coplanar paths, and export stitched millimeter SVG and DXF outlines.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "feature-measurement-workbench",
    slug: "feature-measurement-workbench",
    title: "Feature Measurement Workbench",
    summary: "Collect exact selected point, edge, face-angle, triangle-clearance, fitted radius, selected-wall, and cross-section evidence from one bounded STL.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "archive-safety-context-inspector",
    slug: "archive-safety-context-inspector",
    title: "Archive Safety & Context Inspector",
    summary: "Inspect bounded ZIP and 3MF directory metadata for traversal, encryption, links, executables, nesting, expansion ratios, and adjacent model/context files without extraction.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "license-provenance-checker",
    slug: "license-provenance-checker",
    title: "License & Provenance Checker",
    summary: "Hash a bounded local model, surface embedded and adjacent evidence candidates, record an explicit license declaration, and export a portable review-gated provenance manifest.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "clearance-coupon",
    slug: "sliding-clearance-coupon",
    title: "Profile-Aware Sliding Clearance Coupon",
    summary: "Generate a measured gauge-and-tab ladder centered on the sliding clearance saved in this browser's printer profile.",
    category: "calibrate",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "svg-relief",
    slug: "safe-svg-relief-plate",
    title: "Safe SVG Relief Plate",
    summary: "Validate a passive simple-shape SVG subset, rasterize it to a bounded grid, and export a verified raised-plate STL.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "image-relief",
    slug: "image-relief-lithophane",
    title: "Image Relief & Lithophane Studio",
    summary: "Turn a bounded PNG or JPEG into a rectangular relief or thickness-mapped lithophane and verify the generated STL.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "text-nameplate",
    slug: "text-nameplate-generator",
    title: "Text Nameplate Generator",
    summary: "Rasterize up to 64 characters onto a measured rectangular plate, add raised text, and export a verified binary STL.",
    category: "create",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-preflight",
    slug: "gcode-preflight-motion-map",
    title: "G-code Preflight & Motion Map",
    summary: "Inspect bounded plain-text G-code for motion bounds, temperatures, metadata, risky commands, and a sampled layer-aware endpoint map.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-layer-viewer",
    slug: "gcode-layer-viewer-simulator",
    title: "G-code Layer Viewer & Simulator",
    summary: "Scrub a bounded local toolpath by layer and color motion by slicer feature, speed, estimated flow, tool, or temperature with travel, retraction, and tool-change evidence.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-metadata",
    slug: "gcode-metadata-thumbnail-extractor",
    title: "G-code Metadata & Thumbnail Extractor",
    summary: "Extract recognized slicer, material, profile, time, bounds, command statistics, and validated embedded PNG/JPEG thumbnails from bounded local G-code without executing or uploading it.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-anonymizer",
    slug: "gcode-anonymizer-private-share-copy",
    title: "G-code Anonymizer & Private Share Copy",
    summary: "Remove bounded thumbnail blocks and privacy-looking comment metadata while proving the ordered non-comment command stream remained exactly unchanged.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-compare",
    slug: "gcode-compare-semantic-diff",
    title: "G-code Compare & Semantic Diff",
    summary: "Compare two complete bounded G-code files locally across settings, layers, features, declared and nominal time, extrusion, flow-volume, temperatures, bounds, and motion counts without executing or uploading commands.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-start-end-linter",
    slug: "start-end-gcode-linter",
    title: "Start & End G-code Linter",
    summary: "Statically review bounded start/end scripts for firmware-flavor signals, inherited modes, homing order, temperature waits and limits, extrusion readiness, disruptive commands, shutdown coverage, and state leaks without executing macros.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-pause-color-inserter",
    slug: "gcode-pause-color-change-inserter",
    title: "G-code Pause & Color-Change Inserter",
    summary: "Insert one reviewed firmware-specific pause or filament-change command immediately before a selected printable layer, then reopen and verify the complete modified G-code locally.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "gcode-object-skip",
    slug: "gcode-native-object-skip-helper",
    title: "G-code Native Object-Skip Helper",
    summary: "Pre-skip exactly one object only when complete native Klipper EXCLUDE_OBJECT or Marlin M486 metadata is proven, then reopen the complete modified file without deleting toolpaths.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "printer-profile-compatibility",
    slug: "printer-profile-compatibility-checker",
    title: "Printer & Profile Compatibility Checker",
    summary: "Compare complete local G-code or a rotation-aware STL envelope with confirmed printer dimensions, hardware, temperatures, materials, firmware, and command capabilities without claiming print safety.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "slicer-profile-diff",
    slug: "slicer-profile-semantic-diff-viewer",
    title: "Slicer Profile Semantic Diff Viewer",
    summary: "Compare bounded Prusa-style INI, Cura CFG/definition JSON, and Orca/Bambu JSON profiles locally with disclosed semantic aliases and unchanged unknown keys.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "component-export",
    slug: "component-explorer-export",
    title: "STL Component Explorer & Export",
    summary: "Find exact-coordinate connected components, rank them by triangle count, and export every component as a verified STL.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "plane-cut",
    slug: "axis-plane-cut-cap",
    title: "Axis Plane Cut & Cap",
    summary: "Cut a triangulated STL on X, Y, or Z, reconstruct simple boundary loops, cap both sides, and verify both outputs.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "build-volume-splitter",
    slug: "plain-seam-build-volume-splitter",
    title: "Registered Build-Volume Splitter",
    summary: "Split an oversized STL into verified build-volume-safe pieces with deterministic tapered-tip pins, clearance sockets, and an assembly map.",
    category: "plan",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "three-mf-inspector",
    slug: "3mf-package-inspector",
    title: "3MF Package Inspector",
    summary: "Inspect ZIP structure, core model counts, units, build items, resources, and extensions with archive-bomb limits.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "stl-to-obj",
    slug: "stl-to-obj",
    title: "STL to OBJ Converter",
    summary: "Convert STL triangles to an exact-coordinate-deduplicated OBJ and reopen the output before download.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "obj-to-stl",
    slug: "obj-to-stl",
    title: "OBJ to STL Converter",
    summary: "Triangulate OBJ polygon faces into a binary STL and reopen the output before download.",
    category: "convert",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "obj-inspector",
    slug: "obj-viewer",
    title: "OBJ Viewer & Measurements",
    summary: "Orbit a complete bounded OBJ in WebGL2, inspect topology and dimensions, measure faces, check build fit, and export a private-by-default report.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "mesh-health",
    slug: "mesh-health",
    title: "STL Mesh Health Report",
    summary: "Report exact degenerate and duplicate triangles plus boundary and non-manifold edges without changing the source.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "conservative-repair",
    slug: "conservative-stl-repair",
    title: "Conservative STL Repair",
    summary: "Remove only exact-zero-area and exact-duplicate triangles, then reopen and report the generated STL.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "spool-sufficiency",
    slug: "spool-sufficiency",
    title: "Spool Remaining & Job Sufficiency",
    summary: "Subtract spool tare and reserve to check whether a weighed spool can finish one or more jobs.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "filament-density",
    slug: "filament-density-estimator",
    title: "Filament Density Estimator",
    summary: "Estimate density from a weighed filament sample and propagate entered mass, length, and diameter measurement bounds.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "machine-hourly-cost",
    slug: "machine-depreciation-hourly-cost",
    title: "Machine Depreciation & Hourly Cost",
    summary: "Calculate straight-line depreciation, maintenance, and a disclosed finance proxy per productive machine hour.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "electricity-session-cost",
    slug: "electricity-print-session-cost",
    title: "Electricity & Print-Session Cost",
    summary: "Price measured-average printer, enclosure, accessory, and dryer energy across peak and off-peak time.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "quote-margin-break-even",
    slug: "quote-margin-break-even",
    title: "Quote, Margin & Break-Even",
    summary: "Solve list and discounted unit prices from fulfillment cost, setup, quantity, channel fees, and target net margin.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "resin-true-cost",
    slug: "resin-print-true-cost",
    title: "Resin Print True-Cost Worksheet",
    summary: "Model resin, supports, vat loss, cleaning consumables, film wear, wash/cure labor, machine time, and expected failed runs.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "batch-quantity-economics",
    slug: "batch-quantity-economics",
    title: "Batch & Quantity Economics",
    summary: "Amortize setup and expected plate attempts across a delivered quantity with explicit plate failure, labor, time, and unused capacity.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "shipping-packaging",
    slug: "shipping-packaging-estimator",
    title: "Shipping & Packaging Estimator",
    summary: "Compare actual and dimensional parcel weight using user-entered billing assumptions, packaging materials, and packing labor.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "print-farm-capacity",
    slug: "print-farm-capacity-planner",
    title: "Print-Farm Capacity Planner",
    summary: "Model aggregate machine-hour and unit capacity across a planning horizon with utilization, maintenance, average jobs, failures, and commitments.",
    category: "plan",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "nozzle-line-layer",
    slug: "nozzle-line-layer-sanity",
    title: "Nozzle, Line Width & Layer Sanity",
    summary: "Compare planned line width and layer height with an explicit, editable nozzle-relative envelope without claiming universal print rules.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "acceleration-distance",
    slug: "acceleration-speed-distance",
    title: "Acceleration-to-Speed Distance",
    summary: "Model constant-acceleration distance, peak speed, cruise span, and feature time from entered boundary speeds and feature length.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "axis-shrinkage",
    slug: "xyz-shrinkage-compensation",
    title: "X/Y/Z Shrinkage Compensation",
    summary: "Derive independent linear X, Y, and Z scale corrections from nominal, measured, and target coupon dimensions.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "leadscrew-motion",
    slug: "leadscrew-steps-motion",
    title: "Leadscrew, Gearing & Steps/mm",
    summary: "Calculate screw lead, pulley ratio, full steps/mm, microsteps/mm, motion increments, and motor rotation distance.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "purge-waste",
    slug: "purge-volume-waste",
    title: "Purge Volume & Waste",
    summary: "Convert user-tested transition and fixed purge volumes into filament length, mass, material cost, captured purge-object material, and discard.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "filament-change-layer",
    slug: "filament-change-layer-finder",
    title: "Filament-Change Layer Finder",
    summary: "Map a desired color-start Z to the first uniform layer start at or above it, with the nearest lower layer and explicit delay.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "dry-box-service",
    slug: "desiccant-dry-box-estimator",
    title: "Desiccant & Dry-Box Estimator",
    summary: "Estimate air-only initial moisture load, leakage ingress, entered desiccant capacity, and a modeled service interval with explicit omissions.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "resin-exposure-interpolation",
    slug: "resin-exposure-interpolation",
    title: "Resin Exposure Interpolation",
    summary: "Bilinearly interpolate a starting exposure only inside four user-tested layer-height and resin-temperature corner measurements.",
    category: "calibrate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "maintenance-interval",
    slug: "printer-maintenance-interval-planner",
    title: "Printer Maintenance Interval Planner",
    summary: "Combine user-entered usage and calendar service intervals into an earliest-due forecast without inventing a maintenance schedule.",
    category: "plan",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "belt-frequency-tension",
    slug: "belt-frequency-tension-worksheet",
    title: "Belt Frequency & Tension Worksheet",
    summary: "Apply the ideal vibrating-span relation to an entered free span, linear mass, measured frequency, uncertainty, and target band.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "strength-load",
    slug: "strength-load-safety-factor-worksheet",
    title: "Strength & Load Safety-Factor Worksheet",
    summary: "Compare a simple axial load and entered reductions with a user-measured coupon allowable and desired safety factor.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "filament-drying-session",
    slug: "filament-drying-session-planner",
    title: "Filament Drying Session Planner",
    summary: "Track qualifying time inside a manufacturer-entered temperature band plus interruptions, cooling, and dry-storage transfer.",
    category: "plan",
    status: "beta",
    execution: "numeric",
  },
  { id:"coupon-corrected-hole", slug:"coupon-corrected-clearance-hole", title:"Coupon-Corrected Clearance Hole", summary:"Convert an entered fastener clearance and measured printed-hole coupon ratio into a corrected CAD diameter.", category:"calculate", status:"beta", execution:"numeric" },
  { id:"insert-boss", slug:"heat-set-insert-boss-worksheet", title:"Heat-Set Insert Boss Worksheet", summary:"Check user-entered insert, pilot-hole, radial-wall, depth, clearance, and bottom-wall dimensions as a geometric envelope.", category:"calculate", status:"beta", execution:"numeric" },
  { id:"thread-engagement", slug:"thread-engagement-turns-worksheet", title:"Thread Engagement Turns Worksheet", summary:"Convert entered pitch, available length, allowances, and desired turns into usable engagement and margin.", category:"calculate", status:"beta", execution:"numeric" },
  { id:"countersink-geometry", slug:"countersink-depth-opening-geometry", title:"Countersink Depth & Opening Geometry", summary:"Calculate ideal conical flush depth, added recess, top opening, and remaining stock from entered fastener geometry.", category:"calculate", status:"beta", execution:"numeric" },
  { id:"attribution-readme", slug:"attribution-readme-generator", title:"Attribution & README Generator", summary:"Format user-supplied creator, source, license, notice, and modification evidence into portable Markdown and plain text.", category:"manage", status:"beta", execution:"numeric" },
  { id:"sellable-license-checklist", slug:"sellable-license-review-checklist", title:"Sellable-License Review Checklist", summary:"Record explicit review of source evidence, commercial-use terms, attribution, remix limits, trademarks, customization, and delivery obligations.", category:"manage", status:"beta", execution:"numeric" },
  { id:"marketplace-fee", slug:"marketplace-fee-net-margin-calculator", title:"Marketplace Fee & Net-Margin Calculator", summary:"Reconcile user-entered order charges, fee bases, advertising, payment, shipping, refund, tax reserve, and product cost.", category:"calculate", status:"beta", execution:"numeric" },
  { id:"print-instruction-card", slug:"customer-print-instruction-card-generator", title:"Customer Print Instruction Card Generator", summary:"Create a portable customer handoff card from entered material, orientation, hardware, assembly, warnings, attribution, and support link.", category:"manage", status:"beta", execution:"browser" },
  { id:"guided-print-triage", slug:"guided-print-failure-triage", title:"Guided Print-Failure Triage", summary:"Rank observed safety, first-layer, timing, repeatability, printer-error, and recent-change evidence before choosing a focused diagnostic.", category:"diagnose", status:"beta", execution:"numeric" },
  { id:"stringing-diagnostic", slug:"stringing-diagnostic", title:"Stringing & Oozing Diagnostic", summary:"Separate hotend safety, material condition, profile, temperature, retraction, nozzle residue, and travel evidence one test at a time.", category:"diagnose", status:"beta", execution:"numeric" },
  { id:"warping-adhesion-diagnostic", slug:"warping-adhesion-diagnostic", title:"Warping & Adhesion Diagnostic", summary:"Rank build-surface, first-layer, official-profile, thermal-environment, lift-timing, and model-footprint evidence.", category:"diagnose", status:"beta", execution:"numeric" },
  { id:"layer-shift-diagnostic", slug:"layer-shift-diagnostic", title:"Layer-Shift Diagnostic", summary:"Rank collision, obstruction, axis resistance, motion hardware, belt/pulley procedure, profile, and repeatability evidence.", category:"diagnose", status:"beta", execution:"numeric" },
  { id:"extrusion-diagnostic", slug:"under-over-extrusion-diagnostic", title:"Under / Over-Extrusion Diagnostic", summary:"Separate safety, first-layer restriction, profile/nozzle/diameter mismatch, filament path, clog, heat-creep, and measured-flow evidence.", category:"diagnose", status:"beta", execution:"numeric" },
  {
    id: "transform-stl",
    slug: "transform-stl",
    title: "Transform, Scale & Mirror STL",
    summary: "Scale, rotate, mirror, normalize, and re-export an STL entirely in a local worker.",
    category: "repair",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "stl-inspector",
    slug: "stl-viewer",
    title: "STL Viewer & Measurements",
    summary: "Orbit a complete bounded STL in WebGL2, inspect topology and dimensions, measure faces, check build fit, and export a private-by-default report.",
    category: "inspect",
    status: "experimental",
    execution: "browser",
  },
  {
    id: "build-volume-fit",
    slug: "build-volume-fit",
    title: "Build-Volume Fit Checker",
    summary: "Check all axis-aligned orientations and find the first orientation that fits a printer.",
    category: "plan",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "print-cost",
    slug: "print-cost",
    title: "Print Cost Calculator",
    summary: "Estimate material, machine, electricity, labor, failure allowance, and margin.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "filament",
    slug: "filament-length-weight",
    title: "Filament Length & Weight",
    summary: "Convert between filament mass and length using diameter and material density.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "volumetric-flow",
    slug: "volumetric-flow",
    title: "Volumetric Flow & Speed",
    summary: "Relate line width, layer height, print speed, and volumetric flow.",
    category: "calculate",
    status: "beta",
    execution: "numeric",
  },
  {
    id: "fit-profile",
    slug: "fit-profile",
    title: "Printer & Fit Profile",
    summary: "Save local build-volume, nozzle, and clearance measurements for later tools.",
    category: "calibrate",
    status: "experimental",
    execution: "numeric",
  }
];

/** Operations reachable from the hobbyist task pages. Retired routes live only in legacyTools. */
export const tools = legacyTools.filter((tool) => !retiredOperationIds.has(tool.id));

export function getCategory(slug: string): ToolCategory | undefined {
  return categories.find((category) => category.slug === slug);
}

export function getTool(category: string, slug: string): ToolCatalogDefinition | undefined {
  return tools.find((tool) => tool.category === category && tool.slug === slug);
}

export function getToolsForCategory(category: string): ToolCatalogDefinition[] {
  return tools.filter((tool) => tool.category === category);
}

export function getLegacyToolById(id: string): ToolCatalogDefinition | undefined {
  return tools.find((tool) => tool.id === id);
}

const threeDimensionalOutputIds = new Set([
  "hardware-pocket-composer", "hollow-drain-generator", "emboss-deboss-label", "mesh-decimator",
  "isotropic-remesh", "smooth-denoise", "flatten-base-pedestal", "texture-displacement",
  "color-inlay-splitter", "paint-material-regions", "boolean-csg-studio", "component-export",
  "plane-cut", "conservative-repair", "transform-stl", "step-to-3mf", "step-to-stl",
  "glb-to-stl", "stl-to-glb", "ply-to-stl", "stl-to-ply", "three-mf-to-stl",
  "multi-stl-to-3mf", "stl-to-obj", "obj-to-stl", "spur-gear", "v-groove-pulley",
  "hose-duct-reducer", "drawer-grid-organizer", "screw-lid-enclosure", "lobed-knob",
  "annular-spacer", "open-box", "svg-relief", "image-relief", "text-nameplate",
  "multi-cavity-tray", "two-part-mold", "contour-multi-piece-mold", "mold-channel-network",
  "silicone-formwork", "open-pour-mold", "flow-ratio", "temperature-tower", "retraction-tower",
  "pressure-advance-tower", "maximum-volumetric-flow", "cornering-control-tower",
  "acceleration-reach-tower", "vfa-speed-tower", "clearance-coupon", "build-volume-splitter",
]);

const twoDimensionalOutputIds = new Set(["section-contour-viewer"]);

const modeLabels: Record<string, string> = {
  "hole-feature-detector": "Find round holes",
  "wall-thickness": "Check thin walls",
  "geometry-compare": "Compare STL files",
  "section-contour-viewer": "View a cross-section",
  "feature-measurement-workbench": "Measure exact features",
  "archive-safety-context-inspector": "Check an archive",
  "license-provenance-checker": "Record source and license",
  "three-mf-inspector": "Open a 3MF package",
  "obj-inspector": "Open an OBJ model",
  "mesh-health": "Check mesh health",
  "stl-inspector": "Open an STL model",
  "hardware-pocket-composer": "Add hardware pockets",
  "hollow-drain-generator": "Hollow and add drains",
  "emboss-deboss-label": "Add raised or recessed text",
  "mesh-decimator": "Reduce file complexity",
  "isotropic-remesh": "Rebuild triangle quality",
  "smooth-denoise": "Smooth a rough model",
  "flatten-base-pedestal": "Flatten or add a base",
  "texture-displacement": "Add surface texture",
  "color-inlay-splitter": "Make color inlays",
  "paint-material-regions": "Paint color regions",
  "boolean-csg-studio": "Combine or carve models",
  "component-export": "Separate model parts",
  "plane-cut": "Cut on a flat plane",
  "conservative-repair": "Fix safe mesh problems",
  "transform-stl": "Resize, rotate, or mirror",
  "step-to-3mf": "STEP to 3MF",
  "step-to-stl": "STEP to STL",
  "glb-to-stl": "GLB to STL",
  "stl-to-glb": "STL to GLB",
  "ply-to-stl": "PLY to STL",
  "stl-to-ply": "STL to PLY",
  "three-mf-to-stl": "3MF to STL",
  "multi-stl-to-3mf": "STL files to 3MF",
  "stl-to-obj": "STL to OBJ",
  "obj-to-stl": "OBJ to STL",
  "spur-gear": "Make a spur gear",
  "v-groove-pulley": "Make a V pulley",
  "hose-duct-reducer": "Make a round adapter",
  "drawer-grid-organizer": "Make a drawer organizer",
  "screw-lid-enclosure": "Make a screw-lid box",
  "lobed-knob": "Make a hand knob",
  "annular-spacer": "Make a washer or spacer",
  "open-box": "Make an open box",
  "svg-relief": "Turn SVG shapes into relief",
  "image-relief": "Make a lithophane or relief",
  "text-nameplate": "Make a text sign",
  "mold-intake-repair": "Check the master model",
  "mold-safety-record": "Review mold safety",
  "multi-cavity-tray": "Make a multi-cavity mold",
  "two-part-mold": "Make a two-part mold",
  "contour-multi-piece-mold": "Make a multi-part mold",
  "mold-channel-network": "Add gates and vents",
  "silicone-formwork": "Make a mold box",
  "mold-feasibility": "Check mold direction",
  "mold-mix": "Calculate mold material",
  "open-pour-mold": "Make an open-pour mold",
  "bed-mesh": "View a bed mesh",
  "resonance-viewer": "Review resonance results",
  "bed-tramming": "Adjust bed screws",
  "extrusion-calibration": "Calibrate filament movement",
  "flow-ratio": "Calibrate material flow",
  "temperature-tower": "Make a temperature tower",
  "retraction-tower": "Make a retraction tower",
  "pressure-advance-tower": "Test pressure advance",
  "maximum-volumetric-flow": "Test maximum flow",
  "cornering-control-tower": "Test corner settings",
  "acceleration-reach-tower": "Test acceleration",
  "vfa-speed-tower": "Test speed artifacts",
  "clearance-coupon": "Make a fit test",
  "resin-exposure-interpolation": "Estimate resin exposure",
  "fit-profile": "Save printer measurements",
  "layer-stack": "Match height to layers",
  "dimensional-compensation": "Correct printed size",
  "resin-cost": "Estimate resin material",
  "spool-sufficiency": "Check remaining filament",
  "filament-density": "Measure filament density",
  "machine-hourly-cost": "Calculate machine cost",
  "electricity-session-cost": "Calculate electricity cost",
  "quote-margin-break-even": "Build a price quote",
  "resin-true-cost": "Calculate resin print cost",
  "batch-quantity-economics": "Price multiple copies",
  "shipping-packaging": "Estimate packing and shipping",
  "nozzle-line-layer": "Check nozzle and layers",
  "acceleration-distance": "Check acceleration distance",
  "axis-shrinkage": "Correct shrink by axis",
  "leadscrew-motion": "Calculate motion steps",
  "purge-waste": "Estimate purge waste",
  "filament-change-layer": "Find a color-change layer",
  "dry-box-service": "Plan dry-box service",
  "belt-frequency-tension": "Check belt frequency",
  "strength-load": "Estimate load margin",
  "coupon-corrected-hole": "Correct a printed hole",
  "insert-boss": "Size an insert boss",
  "thread-engagement": "Check thread engagement",
  "countersink-geometry": "Size a countersink",
  "marketplace-fee": "Calculate selling fees",
  "print-cost": "Estimate print cost",
  "filament": "Convert filament amount",
  "volumetric-flow": "Calculate flow and speed",
  "overhang-map": "Check overhangs",
  "axis-orientation": "Compare print directions",
  "center-of-mass-stability": "Check standing stability",
  "gcode-preflight": "Check G-code safety",
  "gcode-layer-viewer": "View G-code layers",
  "gcode-metadata": "Read G-code details",
  "gcode-anonymizer": "Remove private comments",
  "gcode-compare": "Compare G-code files",
  "gcode-start-end-linter": "Check start and end code",
  "gcode-pause-color-inserter": "Add a pause or color change",
  "gcode-object-skip": "Skip one labeled object",
  "printer-profile-compatibility": "Check printer compatibility",
  "slicer-profile-diff": "Compare slicer profiles",
  "build-volume-splitter": "Split an oversized model",
  "print-farm-capacity": "Plan printer capacity",
  "maintenance-interval": "Plan printer maintenance",
  "filament-drying-session": "Plan filament drying",
  "build-volume-fit": "Check build-plate fit",
  "job-manifest": "Make a print job record",
  "attribution-readme": "Make an attribution file",
  "sellable-license-checklist": "Review selling permission",
  "print-instruction-card": "Make customer instructions",
};

const remoteEligibleOperations = new Set(["conservative-repair", "isotropic-remesh", "boolean-csg-studio", "open-pour-mold", "two-part-mold", "multi-cavity-tray", "contour-multi-piece-mold", "mold-channel-network"]);

function acceptedInputsFor(operationId: string): string[] {
  if (operationId.includes("gcode") || operationId.includes("G-code")) return ["gcode"];
  if (operationId.includes("image") || operationId.includes("lithophane") || operationId === "texture-displacement") return ["png", "jpeg"];
  if (operationId.includes("svg")) return ["svg"];
  if (operationId.includes("three-mf") || operationId.includes("3mf")) return ["3mf"];
  if (operationId.startsWith("step-")) return ["step", "stp"];
  if (operationId.startsWith("obj-")) return ["obj"];
  if (operationId.startsWith("ply-")) return ["ply"];
  if (operationId.startsWith("glb-")) return ["glb"];
  if (operationId.includes("stl") || threeDimensionalOutputIds.has(operationId)) return ["stl"];
  return [];
}

function taskMode(operationId: string, level: TaskModeLevel = "basic"): TaskModeDefinition {
  const operation = tools.find((candidate) => candidate.id === operationId);
  if (!operation) throw new Error(`Unknown task operation: ${operationId}`);
  const outputKind: TaskOutputKind = threeDimensionalOutputIds.has(operationId)
    ? "geometry-3d"
    : twoDimensionalOutputIds.has(operationId)
      ? "geometry-2d"
      : "report";
  return {
    id: operationId,
    operationId,
    label: modeLabels[operationId] ?? operation.title,
    level,
    acceptedInputs: acceptedInputsFor(operationId),
    outputKind,
    previewRequired: outputKind === "geometry-2d" || outputKind === "geometry-3d",
    executionPolicy: remoteEligibleOperations.has(operationId) ? "local-or-remote" : "local",
  };
}

function taskPage(
  id: string,
  path: string,
  category: TaskPageDefinition["category"],
  title: string,
  pickerHint: string,
  aliases: string[],
  basicModes: string[],
  advancedModes: string[] = [],
): TaskPageDefinition {
  return {
    id,
    path,
    category,
    title,
    pickerHint,
    seoTitle: `${title} — PartHarbor`,
    seoDescription: pickerHint,
    aliases,
    legacyRoutes: [],
    disclosure: {
      privacy: "Runs on this device unless an eligible overflow job is explicitly confirmed.",
      limits: "Measured file and geometry limits are checked before processing starts.",
      maturity: "Only implemented modes are public; later roadmap work has no placeholder page.",
    },
    modes: [
      ...basicModes.map((mode) => taskMode(mode)),
      ...advancedModes.map((mode) => taskMode(mode, "advanced")),
    ],
  };
}

export const taskEntryPoints: TaskEntryPoint[] = [
  { id: "models", title: "Models", hint: "View, check, repair, edit, and convert model files.", path: "models" },
  { id: "make", title: "Make", hint: "Create practical parts, signs, lithophanes, and molds.", path: "make" },
  { id: "setup", title: "Printer Setup", hint: "Calibrate and maintain filament and resin printers.", path: "setup" },
  { id: "plan", title: "Plan a Print", hint: "Check fit, material, speed, G-code, and job details.", path: "plan" },
  { id: "help", title: "Print Help", hint: "Work through a failed print one safe step at a time.", path: "help" },
  { id: "share", title: "Credits & Sharing", hint: "Record model sources, licenses, and attribution.", path: "share" },
  { id: "sell", title: "Seller Kit", hint: "Price, prepare, and document occasional print sales.", path: "sell" },
];

export const taskPages: TaskPageDefinition[] = [
  taskPage("view-measure", "models/view-measure", "models", "View & Measure", "Open a model and inspect its size, parts, and features.", ["viewer", "measure", "STL", "OBJ", "3MF", "section", "hole"], ["stl-inspector", "obj-inspector", "three-mf-inspector", "feature-measurement-workbench"], ["hole-feature-detector", "section-contour-viewer", "archive-safety-context-inspector"]),
  taskPage("check-model", "models/check", "models", "Check a Model", "Find mesh, wall, overhang, and stability problems.", ["mesh", "wall", "overhang", "printability", "stability"], ["mesh-health", "wall-thickness", "overhang-map"], []),
  taskPage("compare-models", "models/compare", "models", "Compare Models", "See meaningful differences between two STL files.", ["diff", "compare", "revision"], ["geometry-compare"]),
  taskPage("repair-model", "models/repair", "models", "Repair a Model", "Fix safe mesh problems without changing the source file.", ["repair", "watertight", "normals", "holes"], ["conservative-repair"]),
  taskPage("resize-place", "models/resize-place", "models", "Resize & Place", "Resize, rotate, mirror, flatten, or add a printable base.", ["scale", "rotate", "mirror", "lay flat", "pedestal"], ["transform-stl"], ["flatten-base-pedestal"]),
  taskPage("cut-split", "models/cut-split", "models", "Cut & Split", "Separate parts or split an oversized model for printing.", ["cut", "split", "components", "pins", "connectors"], ["component-export", "plane-cut", "build-volume-splitter"]),
  taskPage("combine-carve", "models/combine-carve", "models", "Combine & Carve", "Join, subtract, or keep the overlap between two models.", ["boolean", "CSG", "union", "subtract", "intersect"], ["boolean-csg-studio"]),
  taskPage("hollow-details", "models/hollow-details", "models", "Hollow & Add Details", "Hollow a model or add drains, text, magnets, and inserts.", ["hollow", "drain", "vent", "emboss", "magnet", "insert"], ["hollow-drain-generator", "emboss-deboss-label", "hardware-pocket-composer"]),
  taskPage("simplify-style", "models/simplify-style", "models", "Simplify & Style", "Reduce, smooth, texture, or add printable color regions.", ["decimate", "remesh", "smooth", "texture", "inlay", "paint"], ["mesh-decimator", "smooth-denoise"], ["isotropic-remesh", "texture-displacement", "color-inlay-splitter", "paint-material-regions"]),
  taskPage("convert-files", "models/convert", "models", "Convert Model Files", "Convert common print and CAD formats without uploading.", ["convert", "STL", "OBJ", "PLY", "GLB", "3MF", "STEP"], ["stl-to-obj", "obj-to-stl", "three-mf-to-stl", "multi-stl-to-3mf"], ["stl-to-ply", "ply-to-stl", "stl-to-glb", "glb-to-stl", "step-to-stl", "step-to-3mf"]),

  taskPage("boxes", "make/boxes", "make", "Box & Enclosure Maker", "Make an open box or a fitted screw-lid enclosure.", ["box", "enclosure", "electronics", "lid"], ["open-box", "screw-lid-enclosure"]),
  taskPage("organizers", "make/organizers", "make", "Organizer Maker", "Make a measured drawer organizer with a simple grid.", ["drawer", "organizer", "tray", "grid"], ["drawer-grid-organizer"]),
  taskPage("adapters", "make/adapters", "make", "Adapter Maker", "Make a tapered round hose or duct adapter.", ["hose", "duct", "reducer", "adapter"], ["hose-duct-reducer"]),
  taskPage("knobs-spacers", "make/knobs-spacers", "make", "Knob & Spacer Maker", "Make a hand knob, washer, spacer, or sleeve.", ["knob", "washer", "spacer", "sleeve"], ["lobed-knob", "annular-spacer"]),
  taskPage("gears-pulleys", "make/gears-pulleys", "make", "Gear & Pulley Maker", "Make a spur gear or a single V-groove pulley.", ["gear", "pulley", "involute", "module"], ["spur-gear", "v-groove-pulley"]),
  taskPage("signs-labels", "make/signs-labels", "make", "Sign & Label Maker", "Turn text or simple SVG shapes into printable relief.", ["sign", "label", "nameplate", "SVG", "text"], ["text-nameplate", "svg-relief"]),
  taskPage("images-lithophanes", "make/images-lithophanes", "make", "Image & Lithophane Maker", "Turn a local image into a relief or lithophane.", ["image", "heightmap", "relief", "lithophane"], ["image-relief"]),
  taskPage("molds", "make/molds", "make", "Mold Maker", "Check a master and make common hobby casting molds.", ["mold", "casting", "silicone", "open pour", "two part", "gates", "vents"], ["mold-intake-repair", "mold-feasibility", "open-pour-mold", "two-part-mold", "silicone-formwork", "mold-mix"], ["multi-cavity-tray", "contour-multi-piece-mold", "mold-channel-network", "mold-safety-record"]),

  taskPage("printer-setup", "setup/printer", "setup", "Printer Setup & Maintenance", "Save printer measurements and plan routine maintenance.", ["printer profile", "maintenance", "nozzle", "build volume"], ["fit-profile", "maintenance-interval"]),
  taskPage("bed-first-layer", "setup/bed-first-layer", "setup", "Bed & First Layer", "Review bed measurements and work out screw adjustments.", ["bed mesh", "tramming", "level", "first layer"], ["bed-mesh", "bed-tramming"]),
  taskPage("extrusion", "setup/extrusion", "setup", "Extrusion Calibration", "Calibrate filament movement and material flow.", ["E steps", "rotation distance", "flow ratio", "extrusion multiplier"], ["extrusion-calibration", "flow-ratio"]),
  taskPage("calibration-models", "setup/calibration-models", "setup", "Calibration Model Maker", "Make focused test models for common printer settings.", ["tower", "temperature", "retraction", "pressure advance", "speed"], ["temperature-tower", "retraction-tower", "pressure-advance-tower", "maximum-volumetric-flow"], ["cornering-control-tower", "acceleration-reach-tower", "vfa-speed-tower"]),
  taskPage("resonance-motion", "setup/resonance-motion", "setup", "Resonance & Motion", "Review resonance, belt, and motion calculations.", ["input shaper", "resonance", "belt", "leadscrew", "steps"], ["resonance-viewer", "belt-frequency-tension"], ["leadscrew-motion"]),
  taskPage("fit-holes", "setup/fit-holes", "setup", "Fit & Hole Calibration", "Make fit tests and size holes, inserts, threads, and countersinks.", ["clearance", "coupon", "hole", "insert", "thread", "countersink"], ["clearance-coupon", "coupon-corrected-hole", "insert-boss"], ["thread-engagement", "countersink-geometry"]),
  taskPage("resin-setup", "setup/resin", "setup", "Resin Calibration", "Estimate a cautious resin exposure starting point.", ["resin", "exposure", "layer"], ["resin-exposure-interpolation"]),

  taskPage("build-fit-orientation", "plan/build-fit-orientation", "plan", "Build Fit & Orientation", "Check printer fit, orientation, stability, and compatibility.", ["build volume", "orientation", "stability", "printer compatibility"], ["build-volume-fit", "axis-orientation", "center-of-mass-stability"], ["printer-profile-compatibility"]),
  taskPage("materials", "plan/materials", "plan", "Material & Spool Calculator", "Convert material amounts and check whether a spool is enough.", ["filament", "spool", "density", "resin", "weight", "length"], ["filament", "spool-sufficiency", "filament-density", "resin-cost"]),
  taskPage("speed-flow", "plan/speed-flow", "plan", "Speed & Flow Calculator", "Relate nozzle, layer, speed, flow, and acceleration.", ["volumetric flow", "speed", "nozzle", "layer", "acceleration"], ["volumetric-flow", "nozzle-line-layer", "acceleration-distance"]),
  taskPage("size-fit-strength", "plan/size-fit-strength", "plan", "Size, Fit & Strength", "Correct dimensions and review a basic load margin.", ["scale", "shrinkage", "layers", "strength", "load"], ["layer-stack", "dimensional-compensation", "axis-shrinkage"], ["strength-load"]),
  taskPage("drying-storage", "plan/drying-storage", "plan", "Drying & Storage", "Plan filament drying and dry-box service.", ["dry filament", "dryer", "desiccant", "dry box"], ["filament-drying-session", "dry-box-service"]),
  taskPage("gcode-check", "plan/gcode-check", "plan", "G-code Viewer & Check", "View layers and check commands, settings, and file details.", ["G-code", "viewer", "preflight", "metadata", "lint", "diff", "profile"], ["gcode-layer-viewer", "gcode-preflight", "gcode-metadata"], ["gcode-compare", "gcode-start-end-linter", "slicer-profile-diff"]),
  taskPage("gcode-change", "plan/gcode-change", "plan", "Change G-code", "Add a pause, skip an object, or remove private comments.", ["pause", "color change", "skip object", "anonymize"], ["gcode-pause-color-inserter", "gcode-object-skip"], ["gcode-anonymizer"]),
  taskPage("print-job", "plan/job", "plan", "Print Job Planner", "Plan color changes, purge waste, and a repeatable job record.", ["job", "manifest", "purge", "color change", "batch"], ["filament-change-layer", "purge-waste", "job-manifest"]),

  {
    id: "print-help", path: "help", category: "direct", title: "Print Help Wizard",
    pickerHint: "Work through a failed filament or resin print one step at a time.",
    seoTitle: "3D Print Help Wizard — PartHarbor",
    seoDescription: "A plain-language filament and resin troubleshooting wizard with safe, reversible next steps.",
    aliases: ["diagnose", "troubleshoot", "failed print", "stringing", "warping", "resin"],
    legacyRoutes: [],
    disclosure: { privacy: "Answers stay in this browser session.", limits: "Covers ten common filament and ten common resin symptom families.", maturity: "Guidance is reversible and stops on safety evidence." },
    modes: [{ id: "diagnostic-wizard", operationId: "diagnostic-wizard", label: "Start print help", level: "basic", acceptedInputs: [], outputKind: "report", previewRequired: false, executionPolicy: "local" }],
  },
  taskPage("credits-sharing", "share", "direct", "Credits & Sharing", "Record a model source, license, and required attribution.", ["license", "source", "provenance", "attribution", "README"], ["license-provenance-checker", "attribution-readme"]),
  taskPage("seller-kit", "sell", "direct", "Seller Kit", "Price, prepare, and document occasional print sales.", ["cost", "quote", "fees", "shipping", "batch", "instructions", "license"], ["print-cost", "quote-margin-break-even", "marketplace-fee", "job-manifest", "print-instruction-card", "sellable-license-checklist"], ["machine-hourly-cost", "electricity-session-cost", "resin-true-cost", "batch-quantity-economics", "shipping-packaging", "print-farm-capacity"]),
];

export const taskCategoryOrder = ["models", "make", "setup", "plan"] as const;

export function getTaskPage(path: string): TaskPageDefinition | undefined {
  return taskPages.find((page) => page.path === path.replace(/^\/+|\/+$/g, ""));
}

export function getTaskPagesForCategory(category: TaskPageDefinition["category"]): TaskPageDefinition[] {
  return taskPages.filter((page) => page.category === category);
}

const removedLegacyTools: Record<string, string> = {
  "amf-to-stl": "AMF conversion is outside the hobbyist catalog.",
  "stl-to-amf": "AMF conversion is outside the hobbyist catalog.",
  "off-to-stl": "OFF conversion is outside the hobbyist catalog.",
  "stl-to-off": "OFF conversion is outside the hobbyist catalog.",
  "local-diagnostics": "The internal browser support report is no longer a public utility.",
  "advanced-silicone-formwork": "Industrial support-formwork generation was removed from the hobbyist mold workflow.",
  "matrix-mother-mold": "Industrial mother-mold generation was removed from the hobbyist mold workflow.",
  "plaster-slip-cast": "Specialist plaster slip-casting blocks were removed from the hobbyist mold workflow.",
  "vessel-core-kit": "The specialist vessel core kit was removed from the hobbyist mold workflow.",
  "matched-press": "The specialist die and piston press was removed from the hobbyist mold workflow.",
};

const diagnosticLegacyIds = new Set(["guided-print-triage", "stringing-diagnostic", "warping-adhesion-diagnostic", "layer-shift-diagnostic", "extrusion-diagnostic"]);
const operationTarget = new Map<string, string>();
for (const page of taskPages) {
  for (const mode of page.modes) if (!operationTarget.has(mode.operationId)) operationTarget.set(mode.operationId, page.path);
}
operationTarget.set("job-manifest", "sell");

export const legacyRouteDispositions: Record<string, LegacyRouteDisposition> = Object.fromEntries(
  legacyTools.map((tool) => {
    const legacyPath = `${tool.category}/${tool.slug}`;
    const removedReason = removedLegacyTools[tool.id];
    if (removedReason) return [legacyPath, { kind: "gone", reason: removedReason } satisfies LegacyRouteDisposition];
    if (diagnosticLegacyIds.has(tool.id)) return [legacyPath, { kind: "redirect", targetPath: "help" } satisfies LegacyRouteDisposition];
    const targetPath = operationTarget.get(tool.id);
    if (!targetPath) throw new Error(`Legacy tool ${tool.id} has no hobbyist route disposition.`);
    return [legacyPath, { kind: "redirect", targetPath } satisfies LegacyRouteDisposition];
  }),
);

for (const [legacyRoute, disposition] of Object.entries(legacyRouteDispositions)) {
  if (disposition.kind !== "redirect") continue;
  const page = getTaskPage(disposition.targetPath);
  if (page) page.legacyRoutes.push(legacyRoute);
}
